import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { utils as koilibUtils } from "koilib";
import { get as _get } from "lodash";
import { BigNumber } from "bignumber.js";

// constants

// icons

// components
import { Alert, Box, Button, Card, CardHeader, CircularProgress, Typography, Chip, Modal } from "@mui/material";
import { useWallet } from "../../hooks/useWallet";
import FiltersEarn from "../../components/FiltersEarn";
import EarnTable from "../../components/EarnTable";
import { formatAmount } from "../../utils/format";
import StakingComponent from "../../components/Staking";
import TokenLogo from "../../components/TokenLogo";

// api

// contracts
import { StakingContract } from "../../helpers/contracts";
import { mapSettledWithLimit } from "../../utils/async";
import { farms as ListFarms } from "./../../constants/earn";

const REFRESH_TIMING = 30 * 1000;
// staking contracts read at the same time (public RPC nodes rate limit bursts)
const CONTRACT_CONCURRENCY = 4;

const FarmsPage = () => {
  // Hooks

  // components state
  const [stake, setStake] = useState(null);

  // filters state
  const [filterStake, setFilterStake] = useState(false);
  const [filterStatus, setFilterStatus] = useState("Live");
  const [filterSearch, setFilterSearch] = useState(null);

  // selectors
  const headInfo = useSelector((state) => state.blockchain.headInfo);
  const walletSelector = useWallet();
  const base_tokens = useSelector((state) => state.tokens.base_tokens);
  const custom_tokens = useSelector((state) => state.tokens.custom_tokens);

  // variables
  const provider = _get(walletSelector, "provider", null);
  const signer = _get(walletSelector, "signer", null);

  // contract data of every farm, read in parallel; a farm that fails is left out
  let loadPool = async () => {
    let tokenList = base_tokens.concat(custom_tokens);
    let results = await mapSettledWithLimit(ListFarms, CONTRACT_CONCURRENCY, async (_farm) => {
      try {
        let pool = (await StakingContract(_farm.address, provider, signer).functions.get_pool({})).result;
        return {
          ...pool,
          ..._farm,
          tokenA: tokenList.find((token) => token.address === _get(_farm, "tokenA", "")),
          tokenB: tokenList.find((token) => token.address === _get(_farm, "tokenB", "")),
          tokenReward: tokenList.find((token) => token.address === _get(pool, "tokenReward", "")),
          totalEarn: "0",
          balance: "0",
          totalLiquidity: "0",
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    });
    return results.map((result) => result.value).filter(Boolean);
  };

  // wallet balance and pending reward of every farm
  let loadValues = async (farmsbase = []) => {
    let owner = _get(walletSelector, "wallet[0].address", null);
    let results = await mapSettledWithLimit(farmsbase, CONTRACT_CONCURRENCY, async (_farm) => {
      let balance = null;
      let totalEarn = null;
      if (owner) {
        try {
          let _contractStaking = StakingContract(_farm.address, provider, signer);
          [balance, totalEarn] = await Promise.all([
            _contractStaking.functions.balance_of({ owner }).then(({ result }) => result),
            _contractStaking.functions.get_pending_reward({ owner }).then(({ result }) => result),
          ]);
        } catch (error) {
          console.log(error);
          return null;
        }
      }
      return {
        ..._farm,
        balance: balance ? balance.value : "0",
        totalEarn: totalEarn ? totalEarn.value : "0",
      };
    });
    return results.map((result) => result.value).filter(Boolean);
  };

  const applyFilters = (_farms) => {
    let result = _farms;

    // filter stake
    if (filterStake) {
      result = result.filter((farm) => farm.balance != "0");
    }

    // filter status
    if (filterStatus == "Finished") {
      let blockHead = new BigNumber(_get(headInfo, "head_topology.height", "0"));
      result = result.filter((farm) => {
        let blockEnd = new BigNumber(_get(farm, "blockEnd", "0"));
        if (blockHead.isGreaterThan(blockEnd)) {
          return farm;
        }
      });
    }

    return result;
  };

  // queries: the contract data rarely changes, the wallet values refresh every 30 s while the tab is visible
  const tokensCount = base_tokens.concat(custom_tokens).length;
  const walletAddress = _get(walletSelector, "wallet[0].address", null);
  const poolsQuery = useQuery({
    queryKey: ["farms", "contracts", tokensCount],
    queryFn: () => loadPool(),
    enabled: tokensCount > 0,
    staleTime: 5 * 60 * 1000,
  });
  const valuesQuery = useQuery({
    queryKey: ["farms", "values", walletAddress, tokensCount],
    queryFn: () => loadValues(poolsQuery.data),
    enabled: Boolean(poolsQuery.data),
    refetchInterval: REFRESH_TIMING,
  });
  const farms = valuesQuery.data || [];
  const loading = !valuesQuery.data;

  const loadError = poolsQuery.isError || valuesQuery.isError;
  const retry = () => (poolsQuery.isError ? poolsQuery.refetch() : valuesQuery.refetch());

  if (loading) {
    return (
      <Card
        sx={{
          padding: "1em",
          maxWidth: "400px",
          marginX: "auto",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <CardHeader title={"Farms"} />
        {loadError ? (
          <>
            <Alert variant="outlined" severity="error" sx={{ color: "text.main", marginY: "1em" }}>
              We could not load the farms.
            </Alert>
            <Button
              variant="contained"
              sx={{ borderRadius: "10px" }}
              onClick={retry}
              disabled={poolsQuery.isFetching || valuesQuery.isFetching}
            >
              Retry
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ textAlign: "center", marginTop: "20px" }}>
              <CircularProgress sx={{ marginBottom: "40px" }} />
            </Box>
            <Typography>We are loading the farms...</Typography>
          </>
        )}
      </Card>
    );
  }

  const getImageToken = (token) => <TokenLogo token={token} size={30} />;

  const getStatus = (el) => {
    let blockHead = new BigNumber(_get(headInfo, "head_topology.height", "0"));
    let blockStart = new BigNumber(_get(el, "blockStart", "0"));
    let blockEnd = new BigNumber(_get(el, "blockEnd", "0"));
    if (blockHead.isLessThan(blockStart)) {
      return <Chip size="small" variant="outlined" label="Waiting" color="info" />;
    }
    if (blockHead.isGreaterThan(blockEnd)) {
      return <Chip size="small" variant="outlined" label="Finished" color="error" />;
    }
    return <Chip size="small" variant="outlined" label="Live" color="primary" />;
  };

  // farm rows for the table
  const formatLP = (raw) => `${formatAmount(koilibUtils.formatUnits(raw || "0", 8), { maxDecimals: 6 })} LP`;
  const rows = applyFilters(farms).map((el, index) => ({
    key: _get(el, "address", index),
    tokens: [_get(el, "tokenA", null), _get(el, "tokenB", null)],
    name:
      _get(el, "metadata.name", "") != ""
        ? _get(el, "metadata.name", "")
        : `${_get(el, "tokenA.symbol", "")}-${_get(el, "tokenB.symbol", "")}`,
    status: getStatus(el),
    earned: formatAmount(koilibUtils.formatUnits(_get(el, "totalEarn", "0") || "0", 8), { maxDecimals: 6 }),
    staked: formatLP(_get(el, "balance", "0")),
    total: formatLP(_get(el, "supplyDeposit", "0")),
    onOpen: () => setStake(el),
  }));

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <Card>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <FiltersEarn
            name="Farms"
            search="Search farm..."
            enableStake={true}
            filterStake={filterStake}
            setFilterStake={setFilterStake}
            enableStatus={true}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            enableSearch={false}
            filterSearch={filterSearch}
            setFilterSearch={setFilterSearch}
          />
        </Box>
        <EarnTable
          rows={rows}
          nameLabel="Farm"
          emptyText={farms.length ? "No farms match these filters." : "There are no farms yet."}
        />
      </Card>
      <Modal
        open={!!stake}
        onClose={() => setStake(null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ display: "flex", p: 2 }}
      >
        <Box sx={{ width: "100%", maxWidth: 480, m: "auto" }}>
          <StakingComponent
            type={"farm"}
            name={
              _get(stake, "metadata.name", "") ||
              `${_get(stake, "tokenA.symbol", "")}-${_get(stake, "tokenB.symbol", "")}`
            }
            image={
              <Box sx={{ display: "flex" }}>
                {getImageToken(_get(stake, "tokenA", null))}
                <Box sx={{ ml: "-8px" }}>{getImageToken(_get(stake, "tokenB", null))}</Box>
              </Box>
            }
            onClose={() => setStake(null)}
            {...stake}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default FarmsPage;
